import fileSaver from 'file-saver';

export default angular.module('eventix.common.services.csvExport',[])
    .factory('CSVExport', CSVExport).name;

function CSVExport($filter) {
    /**
     * @ngdoc service
     * @name eventix.common.services.CSVExport
     * @description
     * Service for converting data to CSV
     */
    const humanize = $filter('humanize');
    return { convert: formatCsv };

    /**
     * @ngdoc function
     * @name eventix.common.services.CSVExport#convert
     * @methodOf eventix.common.services.CSVExport
     * @description
     * Format data for CSV
     *
     * Performs the selection of columns, serialization and outputs result to browser.
     * Most browsers will popup a download dialog, except the retarded spawn of Apple,
     * it opens a new page with text output. User has to press Cmd+S to save result.
     *
     * @param {Object[]} data Source data
     * @param {String[]|Object} [columns]
     * Which object keys to export as columns in CSV. You can provide "deep" properties such as
     * `date.location.name`.
     *
     * If you provide an array of property names, a table header is generated by running properties
     * through `humanize` filter. If you provide an object, the keys should be property names and the values their corresponding column
     * headers.
     * @param {String} [name] Filename for the export, defaults to `data_export_[timestamp]`
     * @param {Boolean} [download=true] Whether to offer the file for download
     * @return {String} The CSV file as a string
     */
    function formatCsv(data, columns, name, download = true) {
        if(!data || !data.length)
            return new Error('Data not found');
        var def = getColumnDefinition(columns);
        if(!def) {
            let keys = _.keys(data[0]);
            def = {
                keys: keys,
                names: _.map(keys, humanize)
            };
        }

        let rows = _.map(data, pickColumns.bind(null, def.keys));
        rows.unshift(_.map(def.names, escape).join(','));
        let csv = rows.join('\r\n');
        if(download)
            fileSaver.saveAs(new Blob([csv], {type: 'text/csv;charset=utf-8'}), filename(name));
        return csv;
    }

    /**
     * Select values from object for the columns in CSV
     *
     * @param {String[]} keys Array of properties to select
     * @param {Object} object Source object
     * @return {String} A CSV row
     */
    function pickColumns(keys, object) {
        var picked = [];
        for(let i = 0; i < keys.length; i++) {
            let key = keys[i];
            let complexSelector = /\[.+?\]|\*/;
            // This is a simple column selector, just push result to array
            if(!complexSelector.test(key)) {
                picked.push(_.get(object, keys[i], ''));
                continue;
            }
            // Split key by `.` that is not within `[]`
            let segments = key.split(/\.(?!.*?\])/g);
            let filterRegex = /(.+?)\[(.+?)\]/;
            var temp = object;
            // Lets loop over the segments of this key, saving the result of each segment in `temp`
            //
            // Example: `products.*.event_dates[location=test].start` has 4 segments
            // `[products, *, event_dates[location=test], start]`
            for(let j = 0; j < segments.length; j++) {
                let segment = segments[j];
                // This is a simple segment, aka property name
                if(!complexSelector.test(segment)) {
                    if(_.isArray(temp))
                        temp = _.map(temp, segment);
                    else
                        temp = temp[segment];
                    continue;
                }
                // This is an array selector, aka tickets.*.name
                if(segment === '*' && segments.length > j + 1) {
                    // We immediately consume the next segment
                    segment = segments[++j];
                    // Check if this segment contains a selector like `[name=test]`
                    if(!complexSelector.test(segment)) {
                        temp = _.map(temp, segment);
                        continue;
                    }
                    // This is a complex array selector `tickets.*.products[name=Access product]`
                    let selector = filterRegex.exec(segment);
                    temp = filterByExpr(temp[selector[1]], selector[2]);
                }
                // This is a complex selector like tickets[name=Test]
                let selector = filterRegex.exec(segment);
                temp = filterByExpr(temp[selector[1]], selector[2]);
            }
            picked.push(temp);
        }
        picked = _.map(picked, serialEscape);
        return picked.join(',');
    }

    /**
     * Filter an array by a CSS-like expression
     *
     * @param {Object[]} arr Array of objects to filter
     * @param {String} expr Filter expression
     * Examples: `vat=21`, `name*=Access for`, `vat=21,type=normal`
     *
     * @return {Object[]} Objects that fulfil expression
     */
    function filterByExpr(arr, expr) {
        var conditions = expr.split(',').map(condition => {
            var parts = condition.split(/\*?\=/);
            return {
                key: parts[0],
                loose: condition.indexOf('*') > 0,
                value: parts[1] || true
            };
        });
        return _.filter(arr, item => {
            for(let i = 0; i < conditions.length; i++) {
                let value = _.get(item, conditions[i].key);
                if(conditions[i].loose)
                    return value.indexOf(conditions[i].value) >= 0;
                if(value != conditions[i].value) // eslint-disable-line eqeqeq
                    return false;
            }
            return true;
        });
    }

    /**
     * Get filename for export
     *
     * @param {String} [name] Base filename
     * @return {String} filename
     */
    function filename(name) {
        if(name)
            return name + (name.indexOf('.csv') >= 0 ? '' : '.csv');
        return `data_export_${ Date.now().toString().slice(0, -3) }.csv`;
    }

    /**
     * Serialize and escape a value
     *
     * @param {Any} value A value that should be inserted into the CSV safely
     * @return {String} Safe string
     */
    function serialEscape(value) {
        return escape(serialize(value));
    }

    /**
     * Escape a value for CSV
     *
     * If value contains anything besides an alphanumeric value, it's surrounded by double quotes
     *
     * @param {Any} value A value that should be inserted into the CSV safely
     * @return {String} Safe string
     */
    function escape(value) {
        if(/[^a-zA-Z.0-9\s\-]/.test(value))
            return `"${ value.replace(/"/g, '""') }"`;
        return value;
    }

    /**
     * Serialize an object for human consumption
     *
     * Output resembles YAML.
     *
     * - Nested properties are indented.
     * - Keys are seperated from values by `:`.
     * @param {Any} value Target for serialization
     * @param {Number} [depth=0] Depth of indentation (function is called recursively)
     * @return {String} Serialized output
     */
    function serialize(value, depth = 0) {
        if(_.isArray(value)) {
            var simpleArray = _.isNil(_.find(value, _.isObject));
            if(simpleArray)
                return value.join(', ');
            // fallthrough to object, since arrays are also objects
        }
        if(_.isObject(value)) {
            let prefix = _.times(depth, () => '  ').join('');
            value = _.map(value, (value, key) => {
                return prefix + key + ': ' + serialize(value, depth + 1);
            });
            return (depth ? '\n' : '') + value.join('\n');
        }
        return value;
    }

    /**
     * Get the list of columns to export
     *
     * @param {String[]|Object} [columns] Which object keys to export as columns in CSV. You can provide "deep"
     * properties such as `date.location.name`.
     *
     * If you provide an array of property names, a table header is generated by running properties through `humanize`
     * filter. If you provide an object, the keys should be property names and the values their corresponding column
     * headers.
     * @return {Object[]} Object containing an array of `keys` and `names` corresponding to the keys
     */
    function getColumnDefinition(columns) {
        if(_.isArray(columns) && columns.length > 0) {
            return {
                keys: columns,
                names: _.map(columns, humanize)
            };
        } else if(_.isObject(columns)) {
            return {
                keys: _.keys(columns),
                names: _.values(columns)
            };
        }
        return null;
    }
}
